import { NS } from '@ownyou/shared-types';
import type { SourceData } from '@ownyou/ikigai';

type NamespaceTuple = readonly [string, ...string[]];

export interface IkigaiSourceInputStore {
  list<T>(namespace: NamespaceTuple, options?: { limit?: number; offset?: number }): Promise<{
    items: T[];
    hasMore?: boolean;
  }>;
}

export type IkigaiSourceInputKind = 'email' | 'calendar' | 'financial' | 'browsing';

export interface IkigaiSourceInputLoadIssue {
  source: IkigaiSourceInputKind;
  namespace: string;
  reasonCode: 'source-list-failed';
  message: string;
}

export interface IkigaiSourceInputSummary {
  loadedSources: IkigaiSourceInputKind[];
  counts: {
    sourceCount: number;
    emailCount: number;
    calendarCount: number;
    financialCount: number;
    browsingCount: number;
  };
}

export interface IkigaiSourceInputDiagnostics {
  userId: string;
  emailQuery?: {
    namespace: NamespaceTuple;
    totalScanned: number;
    pagesRead: number;
    emailItemsFound: number;
  };
  emailsAfterFilter?: number;
  sourceCounts: IkigaiSourceInputSummary['counts'];
  errors: IkigaiSourceInputLoadIssue[];
}

export interface ReadIkigaiSourceInputOptions {
  store: IkigaiSourceInputStore;
  userId: string;
  filterNewsletters?: boolean;
  emailPageSize?: number;
  maxEmailPages?: number;
}

export interface ReadIkigaiSourceInputResult {
  sourceData: SourceData;
  summary: IkigaiSourceInputSummary;
  diagnostics: IkigaiSourceInputDiagnostics;
}

function namespaceName(namespace: NamespaceTuple): string {
  return namespace[0] ?? 'unknown';
}

function sourceListIssue(
  source: IkigaiSourceInputKind,
  namespace: NamespaceTuple,
  error: unknown,
): IkigaiSourceInputLoadIssue {
  return {
    source,
    namespace: namespaceName(namespace),
    reasonCode: 'source-list-failed',
    message: error instanceof Error ? error.message : String(error),
  };
}

async function storeList(
  store: IkigaiSourceInputStore,
  namespace: NamespaceTuple,
  limit = 500,
): Promise<Record<string, unknown>[]> {
  const result = await store.list<Record<string, unknown>>(namespace, { limit, offset: 0 });
  return result?.items ?? [];
}

function applyNewsletterFilter(emails: NonNullable<SourceData['email']>): NonNullable<SourceData['email']> {
  if (emails.length === 0) return emails;

  const senderCounts = new Map<string, number>();
  for (const email of emails) {
    const key = email.sender.toLowerCase().trim();
    senderCounts.set(key, (senderCounts.get(key) ?? 0) + 1);
  }

  const newsletterPatterns = /noreply|no-reply|mailer-daemon|^bounce@|^bulk@/i;
  const bulkThreshold = 10;
  return emails.filter(email => {
    const key = email.sender.toLowerCase().trim();
    if ((senderCounts.get(key) ?? 0) >= bulkThreshold) return false;
    if (newsletterPatterns.test(key)) return false;
    return true;
  });
}

export function summarizeIkigaiSourceInput(sourceData: SourceData): IkigaiSourceInputSummary {
  const loadedSources = (['email', 'calendar', 'financial', 'browsing'] as const).filter(source => {
    const values = sourceData[source];
    return Array.isArray(values) && values.length > 0;
  });

  return {
    loadedSources: [...loadedSources],
    counts: {
      sourceCount: loadedSources.length,
      emailCount: sourceData.email?.length ?? 0,
      calendarCount: sourceData.calendar?.length ?? 0,
      financialCount: sourceData.financial?.length ?? 0,
      browsingCount: sourceData.browsing?.length ?? 0,
    },
  };
}

export async function readIkigaiSourceInput({
  store,
  userId,
  filterNewsletters = false,
  emailPageSize = 500,
  maxEmailPages = 20,
}: ReadIkigaiSourceInputOptions): Promise<ReadIkigaiSourceInputResult> {
  const sourceData: SourceData = {};
  const errors: IkigaiSourceInputLoadIssue[] = [];
  const diagnostics: IkigaiSourceInputDiagnostics = {
    userId,
    sourceCounts: {
      sourceCount: 0,
      emailCount: 0,
      calendarCount: 0,
      financialCount: 0,
      browsingCount: 0,
    },
    errors,
  };

  const emailNs = NS.iabClassifications(userId);
  try {
    const allEmailItems: Record<string, unknown>[] = [];
    let offset = 0;
    let totalScanned = 0;
    let pagesRead = 0;

    while (pagesRead < maxEmailPages) {
      const rawResult = await store.list<Record<string, unknown>>(emailNs, {
        limit: emailPageSize,
        offset,
      });
      const items = rawResult?.items ?? [];
      pagesRead += 1;
      totalScanned += items.length;

      for (const item of items) {
        if (item.sourceType === 'email') {
          allEmailItems.push(item);
        }
      }

      if (!rawResult?.hasMore || items.length === 0) break;
      offset += emailPageSize;
    }

    diagnostics.emailQuery = {
      namespace: emailNs,
      totalScanned,
      pagesRead,
      emailItemsFound: allEmailItems.length,
    };

    let emails: NonNullable<SourceData['email']> = allEmailItems.map(item => {
      const from = (item.from as string) ?? 'unknown';
      const email: NonNullable<SourceData['email']>[number] = {
        sender: from,
        subject: (item.summary as string) ?? '',
        date: (item.date as string) ?? (item.timestamp as string) ?? '',
      };
      const senderName = from.split('<')[0]?.trim();
      const body = (item.content as string | undefined)?.substring(0, 200);
      if (senderName) email.senderName = senderName;
      if (body) email.body = body;
      return email;
    });

    if (filterNewsletters) {
      emails = applyNewsletterFilter(emails);
    }

    diagnostics.emailsAfterFilter = emails.length;
    if (emails.length > 0) {
      sourceData.email = emails;
    }
  } catch (error) {
    errors.push(sourceListIssue('email', emailNs, error));
  }

  const calendarNs = NS.calendarEvents(userId);
  try {
    const calItems = await storeList(store, calendarNs);
    if (calItems.length > 0) {
      sourceData.calendar = calItems.map(item => ({
        title: (item.summary as string) ?? (item.title as string) ?? '',
        startTime: (item.start as string) ?? (item.startTime as string) ?? '',
        endTime: (item.end as string) ?? (item.endTime as string),
        location: item.location as string | undefined,
        attendees: item.attendees as string[] | undefined,
        isRecurring: item.isRecurring as boolean | undefined,
      }));
    }
  } catch (error) {
    errors.push(sourceListIssue('calendar', calendarNs, error));
  }

  const financialNs = NS.financialTransactions(userId);
  try {
    const finItems = await storeList(store, financialNs);
    if (finItems.length > 0) {
      sourceData.financial = finItems.map(item => ({
        merchant:
          (item.merchantName as string) ??
          (item.merchant as string) ??
          (item.name as string) ??
          '',
        amount: (item.amount as number) ?? 0,
        category: item.category as string | undefined,
        date: (item.date as string) ?? (item.transactionDate as string) ?? '',
      }));
    }
  } catch (error) {
    errors.push(sourceListIssue('financial', financialNs, error));
  }

  const browsingNs = NS.browsingHistory(userId);
  try {
    const browseItems = await storeList(store, browsingNs);
    if (browseItems.length > 0) {
      sourceData.browsing = browseItems.map(item => ({
        url: (item.url as string) ?? '',
        title: (item.title as string) ?? undefined,
        visitCount: (item.visitCount as number) ?? 1,
        totalDuration: (item.duration as number) ?? undefined,
        lastVisitTime: (item.lastVisitTime as string) ?? '',
      }));
    }
  } catch (error) {
    errors.push(sourceListIssue('browsing', browsingNs, error));
  }

  const summary = summarizeIkigaiSourceInput(sourceData);
  diagnostics.sourceCounts = summary.counts;

  return { sourceData, summary, diagnostics };
}
